// Copyright 1999-2026. WebPros International GmbH. All rights reserved.

import { useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Transition } from 'react-transition-group';
import { parsePath, matchPath, useLocation } from 'react-router';
import classNames from 'classnames';
import { Tooltip, Text } from '@plesk/ui-library';
import { prepareUrl } from 'jsw';
import { useLocalStorage } from 'common/hooks';
import ViewSwitcher from './ViewSwitcher';
import Brand from '../Brand';
import Link from 'components/common/Link';
import { useQuery } from '@apollo/client/react';

import MAIN_MENU_QUERY from 'queries/MainMenu.graphql';

const Node = ({
    id = undefined,
    styleClass = undefined,
    active = false,
    disabled = false,
    name,
    conhelp,
    tooltipAsLabel = false,
    link,
    target = undefined,
    custom = false,
    icon = undefined,
    label = undefined,
    onClick = undefined,
}) => (
    <li
        id={id || null}
        className={classNames(!id && styleClass, active && 'active', disabled && 'page-sidebar-menu-item--disabled')}
    >
        <Tooltip title={conhelp} asLabel={tooltipAsLabel}>
            <Link
                to={prepareUrl(link)}
                target={target}
                onClick={onClick}
                disabled={disabled}
            >
                <i
                    className={styleClass ? `icon-${styleClass.split(' ')[0]}` : null}
                    style={custom ? { backgroundImage: `url('${icon}')` } : null}
                />
                <span className="title">
                    <span className="title-name">{name}</span>
                    {!!label && (
                        <Text fontSize="sm" className="title-label">{label}</Text>
                    )}
                </span>
            </Link>
        </Tooltip>
    </li>
);

Node.propTypes = {
    id: PropTypes.string,
    styleClass: PropTypes.string,
    active: PropTypes.bool,
    disabled: PropTypes.bool,
    name: PropTypes.string.isRequired,
    conhelp: PropTypes.string.isRequired,
    tooltipAsLabel: PropTypes.bool,
    link: PropTypes.string.isRequired,
    target: PropTypes.string,
    custom: PropTypes.bool,
    icon: PropTypes.string,
    label: PropTypes.string,
    onClick: PropTypes.func,
};

const Group = ({
    id,
    name = null,
    nodes,
    activeNode = undefined,
    statistics = undefined,
    isSidebarClosed,
    onNodeClick = null,
}) => {
    const nodeRef = useRef(null);
    const group = id.toString().replace('_', '-');
    const [isHidden, setHidden] = useLocalStorage(`menu-group-${group}-hidden`);

    let title = (
        <button
            className="menu-group-title"
            aria-expanded={isHidden === 'true' ? 'false' : 'true'}
            aria-controls={`menuId-${group}`}
            onClick={() => setHidden(isHidden === 'true' ? 'false' : 'true')}
            type="button"
        >
            <span className="menu-group-toggle" />
            <span className="menu-group-name">{name}</span>
        </button>
    );

    if (isSidebarClosed && name) {
        title = (
            <Tooltip title={name} asLabel>
                {title}
            </Tooltip>
        );
    }

    return (
        <li key={id} className={`menu-group-${group}`}>
            {id === 'general' ? null : title}
            {nodes && Object.keys(nodes).length ? (
                <Transition nodeRef={nodeRef} in={isHidden !== 'true'} timeout={200} mountOnEnter unmountOnExit>
                    <ul ref={nodeRef} className="sub-menu" id={`menuId-${group}`}>
                        {Object.entries(nodes).map(([key, { conhelp, ...props }]) => (
                            <Node
                                key={key}
                                active={key === activeNode}
                                conhelp={isSidebarClosed ? props.name : conhelp}
                                tooltipAsLabel={isSidebarClosed}
                                onClick={onNodeClick}
                                label={statistics?.[(key ?? '').replace(/([-_]\w)/g, g => g[1].toUpperCase())]}
                                {...props}
                            />
                        ))}
                    </ul>
                </Transition>
            ) : null}
        </li>
    );
};

Group.propTypes = {
    id: PropTypes.string.isRequired,
    name: PropTypes.string,
    nodes: PropTypes.object.isRequired,
    activeNode: PropTypes.string,
    statistics: PropTypes.object,
    isSidebarClosed: PropTypes.bool.isRequired,
    onNodeClick: PropTypes.func,
};

const getFirstLink = navigation => {
    for (const group of navigation) {
        const foundNode = Object.values(group.nodes).find(node => node.link);

        if (foundNode) {
            return foundNode.link;
        }
    }
};

const PageSidebar = ({
    title,
    navigation,
    navigationContext = undefined,
    breadcrumbs = [],
    viewSwitcher = undefined,
    isPowerUserPanel = false,
    showStatistics = false,
    isClosed = false,
    onNodeClick = undefined,
}) => {
    const location = useLocation();
    const { data: { viewer: { statistics } = {} } = {} } = useQuery(MAIN_MENU_QUERY, {
        skip: !showStatistics,
    });

    const activeNode = useMemo(() => {
        const getExtension = url => url.match(/\/modules\/([^/]+)/)?.[1];
        const breadcrumbPathname = parsePath(breadcrumbs?.[(isPowerUserPanel || breadcrumbs.length < 2) ? 0 : 1]?.href ?? '').pathname ?? '';
        const breadcrumbExtension = getExtension(breadcrumbPathname);
        const locationPathname = location.pathname;
        const locationExtension = getExtension(locationPathname);

        for (const group of navigation) {
            const foundNode = Object.entries(group.nodes).find(([, node]) => {
                const nodePathname = parsePath(node.link).pathname;
                if (!nodePathname) {
                    return false;
                }
                if (matchPath(nodePathname, breadcrumbPathname) || locationPathname.startsWith(nodePathname)) {
                    return true;
                }

                const nodeExtension = getExtension(nodePathname);
                if (!nodeExtension) {
                    return false;
                }
                return nodeExtension === breadcrumbExtension || nodeExtension === locationExtension;
            });

            if (foundNode) {
                return foundNode[0];
            }
        }
    }, [navigation, breadcrumbs, isPowerUserPanel, location.pathname]);

    return (
        <div
            className={classNames(
                'page-sidebar',
                isClosed && 'page-sidebar--folded',
            )}
            data-type="page-sidebar"
        >
            <div className="page-sidebar__content">
                <div className="page-sidebar-brand">
                    {isClosed ? (
                        <div className="brand-collapsed">{title[0] || 'P'}</div>
                    ) : (
                        <Brand href={getFirstLink(navigation)} />
                    )}
                </div>
                <div className="page-sidebar-menu-wrapper">
                    <ul className="page-sidebar-menu">
                        {navigation.map(({ id, ...props }) => (
                            <Group
                                key={id}
                                id={id}
                                isSidebarClosed={isClosed}
                                onNodeClick={onNodeClick}
                                activeNode={activeNode || navigationContext}
                                statistics={id === 'hosting' ? statistics : null}
                                {...props}
                            />
                        ))}
                    </ul>
                </div>
            </div>
            {viewSwitcher ? (
                <div className="page-sidebar-footer-wrapper">
                    <div className="page-sidebar-footer">
                        {<ViewSwitcher {...viewSwitcher} />}
                    </div>
                </div>
            ) : null}
        </div>
    );
};

PageSidebar.propTypes = {
    title: PropTypes.string.isRequired,
    navigation: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.string,
        showTitle: PropTypes.bool,
        name: PropTypes.string,
        nodes: PropTypes.object,
    })).isRequired,
    navigationContext: PropTypes.string,
    breadcrumbs: PropTypes.array,
    viewSwitcher: PropTypes.shape({
        locale: PropTypes.object,
    }),
    isPowerUserPanel: PropTypes.bool,
    showStatistics: PropTypes.bool,
    isClosed: PropTypes.bool,
    onNodeClick: PropTypes.func,
};

export default PageSidebar;
